<template>
  <section>
    <nav class="level">
      <div class="level-left">
        <div class="level-item">
          <b-field grouped group-multiline>
            <b-field>
              <b-input
                v-model="search"
                placeholder="Search..."
                type="search"
                icon="magnify"
                @input="onSearching"
              ></b-input>
              <p class="control">
                <button class="button is-primary" @click="enterSearching">
                  {{ $t('attributes.search') }}
                </button>
              </p>
            </b-field>
          </b-field>
        </div>
      </div>
      <div class="level-right">
        <div class="level-item">
          <button class="button field is-primary" @click="onAdd">
            <b-icon icon="plus"></b-icon>
            <span>{{ $t('attributes.add') + $t('attributes.user') }}</span>
          </button>
        </div>
        <div class="level-item">
          <b-upload
            v-model="file"
            accept="application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            @input="onImport"
          >
            <a class="button is-second" style="cursor: pointer">
              <b-icon icon="upload"></b-icon>
              <span>{{ $t('attributes.import') + $t('attributes.user') }}</span>
            </a>
          </b-upload>
        </div>
      </div>
    </nav>

    <b-collapse
      v-if="importStatus"
      :open="!!importErrors"
      class="card"
      aria-id="contentIdForA11y3"
      style="margin-bottom: 20px;background-color: #E8E8E8;"
    >
      <template #trigger="props">
        <div
          class="card-header"
          role="button"
          aria-controls="contentIdForA11y3"
        >
          <p class="card-header-title">匯入狀態：{{ importStatus }}</p>
          <a class="card-header-icon">
            <b-icon :icon="props.open ? 'menu-down' : 'menu-up'"> </b-icon>
          </a>
        </div>
      </template>
      <div class="card-content" style="padding: 1rem 1.5rem;">
        <div v-if="importErrors" class="content">
          <p
            v-for="(error, index) in importErrors"
            :key="index"
            class="is-marginless"
          >
            {{ error }}
          </p>
        </div>
      </div>
    </b-collapse>

    <b-table
      :data="items"
      :loading="loading"
      :hoverable="true"
      :narrowed="true"
      :mobile-cards="true"
      paginated
      backend-pagination
      backend-sorting
      :total="total"
      :per-page="perPage"
      :default-sort-direction="defaultSortOrder"
      :default-sort="[sortField, sortOrder]"
      aria-next-label="Next page"
      aria-previous-label="Previous page"
      aria-page-label="Page"
      aria-current-label="Current page"
      @page-change="onPageChange"
      @sort="onSort"
    >
      <b-table-column
        field="id"
        :label="$t('attributes.id')"
        :visible="false"
        v-slot="props"
      >
        {{ props.row.id }}
      </b-table-column>

      <b-table-column
        field="user_id"
        :label="$t('attributes.user_id')"
        :visible="false"
        v-slot="props"
      >
        {{ props.row.user_id }}
      </b-table-column>

      <b-table-column
        field="user_account"
        :label="$t('attributes.account')"
        :width="150"
        sortable
        v-slot="props"
      >
        {{ props.row.user_account }}
      </b-table-column>

      <b-table-column
        field="user_name"
        :label="$t('attributes.user_name')"
        :width="150"
        sortable
        v-slot="props"
      >
        {{ props.row.user_name }}
      </b-table-column>

      <b-table-column
        field="user_email"
        :label="$t('attributes.email')"
        :width="150"
        sortable
        v-slot="props"
      >
        {{ props.row.user_email }}
      </b-table-column>

      <b-table-column
        field="member_role"
        :label="$t('attributes.member_role')"
        :width="120"
        sortable
        v-slot="props"
      >
        {{ role_name(props.row.member_role) }}
      </b-table-column>

      <b-table-column
        v-if="regionEnabled"
        field="region"
        :label="regionAlias"
        :width="120"
        sortable
        v-slot="props"
      >
        {{ props.row.region }}
      </b-table-column>

      <b-table-column
        v-if="areaEnabled"
        field="area"
        :label="areaAlias"
        :width="120"
        sortable
        v-slot="props"
      >
        {{ props.row.area }}
      </b-table-column>

      <b-table-column
        v-if="departmentEnabled"
        field="department"
        :label="departmentAlias"
        :width="120"
        sortable
        v-slot="props"
      >
        {{ props.row.department }}
      </b-table-column>

      <b-table-column
        v-if="sectionEnabled"
        field="section"
        :label="sectionAlias"
        :width="120"
        sortable
        v-slot="props"
      >
        {{ props.row.section }}
      </b-table-column>

      <b-table-column
        field="membership"
        :label="$t('attributes.membership')"
        :width="120"
        sortable
        v-slot="props"
      >
        {{ props.row.membership }}
      </b-table-column>

      <b-table-column
        field="member_status"
        :label="$t('attributes.member_status')"
        :width="120"
        sortable
        v-slot="props"
      >
        {{ props.row.member_status }}
      </b-table-column>

      <b-table-column
        field="group_type"
        :label="$t('attributes.group_type')"
        :width="120"
        sortable
        v-slot="props"
      >
        {{ props.row.group_type }}
      </b-table-column>

      <b-table-column
        field="free_quota"
        :label="$t('attributes.free_quota')"
        numeric
        sortable
        v-slot="props"
      >
        {{ props.row.free_quota - 1 }}
      </b-table-column>

      <b-table-column
        field="max_quota"
        :label="$t('attributes.max_quota')"
        numeric
        sortable
        v-slot="props"
      >
        {{ props.row.max_quota }}
      </b-table-column>

      <b-table-column
        field="subsidy"
        :label="$t('attributes.subsidy')"
        numeric
        sortable
        v-slot="props"
      >
        {{ props.row.subsidy }}
      </b-table-column>

      <b-table-column
        :label="$t('attributes.function')"
        :width="150"
        style="min-width: 150px;"
        centered
        v-slot="props"
      >
        <div class="buttons">
          <b-button type="is-primary" @click.prevent="onModify(props.row)">
            {{ $t('attributes.modify') }}
          </b-button>
          <b-button type="is-danger" @click.prevent="onDelete(props.row)">
            {{ $t('attributes.remove') }}
          </b-button>
        </div>
      </b-table-column>
    </b-table>

    <b-modal
      :active.sync="isModalActive"
      :can-cancel="['escape', 'x']"
      has-modal-card
    >
      <activity-user-form
        :activity="activity"
        v-bind="selectedUser"
        :sending="sending"
        @onSend="onSend"
      />
    </b-modal>

    <b-loading :active.sync="isProcessing" :is-full-page="true"></b-loading>
  </section>
</template>

<script>
import debounce from 'lodash/debounce'
import { FIRM_COLLABORATES_ACTIVITIES_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'
import ActivityUserForm from './ActivityUserForm'

export default {
  name: 'ActivityUsers',
  components: { ActivityUserForm },

  props: {
    activity: {
      required: true,
      type: Object
    }
  },
  data() {
    return {
      regionAlias: this.activity.region_alias,
      areaAlias: this.activity.area_alias,
      departmentAlias: this.activity.department_alias,
      sectionAlias: this.activity.section_alias,
      regionEnabled: this.activity.region_enabled,
      areaEnabled: this.activity.area_enabled,
      departmentEnabled: this.activity.department_enabled,
      sectionEnabled: this.activity.section_enabled,

      loading: false,
      items: [],
      total: 0,
      filterStatus: '',
      search: '',
      sortField: 'user_account',
      sortOrder: 'asc',
      defaultSortOrder: 'asc',
      page: 1,
      perPage: 10,

      file: null,

      isModalActive: false,
      selectedUser: null,
      sending: false,

      importStatus: null,
      importErrors: null
    }
  },
  watch: {
    activity(value, old) {
      this.$importHub.leaveActivityImportGroup(old.id)
      this.$importHub.JoinActivityImportGroup(value.id)

      this.importStatus = null
      this.importErrors = null
      this.loadActivityUsers(value.id)
    }
  },
  computed: {
    isProcessing() {
      return this.loading || this.sending
    }
  },
  created() {
    this.$importHub.JoinActivityImportGroup(this.activity.id)

    // Listen to score changes coming from SignalR events
    this.$importHub.$on('import-status', this.onImportStatus)
    this.$importHub.$on('import-error', this.onImportError)
    this.$importHub.$on('import-completed', this.onImportCompleted)
  },
  beforeDestroy() {
    // Make sure to cleanup SignalR event handlers when removing the component
    this.$importHub.$off('import-status', this.onImportStatus)
    this.$importHub.$off('import-error', this.onImportError)
    this.$importHub.$off('import-completed', this.onImportCompleted)

    this.$importHub.leaveActivityImportGroup(this.activity.id)
  },
  mounted() {
    this.loadActivityUsers(this.activity.id)
  },
  methods: {
    async loadActivityUsers(activityId) {
      try {
        const api = `${FIRM_COLLABORATES_ACTIVITIES_API}/${activityId}/users`

        const params = {
          search: this.search,
          sort_by: this.sortField,
          by_descending: this.sortOrder !== 'asc',
          page_size: this.perPage,
          page_number: this.page
        }

        this.loading = true

        const res = await this.axios.get(api, { params })
        const data = res.data.data

        this.items = data.items
        this.total = data.item_count
      } catch (e) {
        this.items = []
        this.total = 0

        notifyService.fetch_error('user', e)
      } finally {
        this.loading = false
      }
    },

    onSearching: debounce(function() {
      this.loadActivityUsers(this.activity.id)
    }, 500),
    enterSearching() {
      this.loadActivityUsers(this.activity.id)
    },
    onPageChange(page) {
      this.page = page
      this.loadActivityUsers(this.activity.id)
    },
    onSort(field, order) {
      this.sortField = field
      this.sortOrder = order
      this.loadActivityUsers(this.activity.id)
    },

    onImport() {
      if (this.file == null) {
        return
      }

      this.importUsers()
    },
    onAdd() {
      this.isEditing = false
      this.selectedUser = {
        date_of_birth: null
      }
      this.isModalActive = true
    },
    onModify(selected) {
      this.isEditing = true
      this.selectedUser = selected
      this.isModalActive = true
    },
    onDelete(selected) {
      this.$buefy.dialog.confirm({
        title: `${this.$t('attributes.remove')} ${this.$t('attributes.user')}`,
        message: `${this.$t('attributes.confirm_remove')} ${
          selected.user_name
        }?`,
        cancelText: this.$t('attributes.cancel'),
        confirmText: this.$t('attributes.confirm'),
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => {
          this.deleteUser(selected)
        }
      })
    },
    onSend(item) {
      if (!this.isEditing) {
        this.createUser(item)
      } else {
        this.updateUser(item)
      }
    },
    async createUser(item) {
      try {
        const api = `${FIRM_COLLABORATES_ACTIVITIES_API}/${this.activity.id}/users`

        this.sending = true
        await this.axios.post(api, item)
        this.isModalActive = false

        notifyService.success(this.$t('attributes.add_succeed'))
        await this.loadActivityUsers(this.activity.id)
      } catch (e) {
        notifyService.create_error(e)
      } finally {
        this.sending = false
      }
    },
    async updateUser(item) {
      try {
        const api = `${FIRM_COLLABORATES_ACTIVITIES_API}/${this.activity.id}/users/${item.id}`

        this.sending = true
        await this.axios.put(api, item)
        this.isModalActive = false

        notifyService.success(this.$t('attributes.update_succeed'))
        await this.loadActivityUsers(this.activity.id)
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.sending = false
      }
    },
    async deleteUser(item) {
      try {
        const api = `${FIRM_COLLABORATES_ACTIVITIES_API}/${this.activity.id}/users/${item.id}`

        this.sending = true
        await this.axios.delete(api)

        notifyService.success(this.$t('attributes.remove_succeed'))
        await this.loadActivityUsers(this.activity.id)
      } catch (e) {
        notifyService.delete_error(e)
      } finally {
        this.sending = false
      }
    },
    async importUsers() {
      try {
        const api = `${FIRM_COLLABORATES_ACTIVITIES_API}/${this.activity.id}/users/import`
        const headers = {
          'Content-Type': 'multipart/form-data'
        }

        const formData = new FormData()
        formData.append('file', this.file)

        this.sending = true
        this.importStatus = null
        this.importErrors = null

        await this.axios.post(api, formData, { headers })

        notifyService.success('開始匯入')
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e)
        notifyService.error('匯入失敗', e)
      } finally {
        this.sending = false
        this.file = null
      }
    },
    onImportStatus({ message }) {
      this.importStatus = message
    },
    onImportError({ message, errors }) {
      this.importStatus = message
      this.importErrors = errors
    },
    onImportCompleted({ message }) {
      this.importStatus = message
      this.loadActivityUsers(this.activity.id)
    }
  }
}
</script>
